<template>
  <v-card outlined class="rounded-lg">
    <v-card-text>
      <!-- Header -->
      <div class="d-flex align-center justify-space-between mb-4">
        <div class="d-flex align-center">
          <v-icon color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
          <span class="subtitle-2 font-weight-black text-uppercase" style="line-height:1.2;">Quarterly<br>Leaderboard</span>
        </div>
        <span class="caption grey--text text-right">Quarter<br>leaders</span>
      </div>

      <!-- Leaderboard rows (top 5 only) -->
      <div v-for="(entry, i) in leaderboard.slice(0, 5)" :key="entry.name"
        class="d-flex align-center pa-3 rounded-lg mb-2"
        :style="entry.isYou
          ? 'background:#fff8e1;border:1px solid #ffe082;'
          : 'background:#f5f5f5;border:1px solid #e0e0e0;'"
      >
        <!-- Rank badge -->
        <div class="mr-3 d-flex align-center justify-center"
          style="width:36px;height:36px;border-radius:50%;flex-shrink:0;"
          :style="i === 0 ? 'background:#fff8e1;' : i === 1 ? 'background:#f0f0f0;' : i === 2 ? 'background:#fce4ec;' : 'background:#e0e0e0;'"
        >
          <v-icon v-if="i === 0" small color="amber darken-2">mdi-crown</v-icon>
          <v-icon v-else-if="i === 1" small color="grey">mdi-medal</v-icon>
          <v-icon v-else-if="i === 2" small color="pink lighten-2">mdi-medal-outline</v-icon>
          <span v-else class="caption grey--text font-weight-bold">{{ i + 1 }}</span>
        </div>

        <span class="body-2 flex-grow-1" :class="entry.isYou ? 'font-weight-black' : 'font-weight-medium'" style="cursor:pointer;" @click.stop="openProfile(entry)">
          {{ entry.name }}
        </span>

        <div class="text-right">
          <span class="subtitle-1 font-weight-black" :class="entry.isYou ? 'primary--text' : ''">
            {{ entry.score.toLocaleString() }}
          </span>
          <span class="caption grey--text ml-1">pts</span>
        </div>
      </div>

      <!-- View full ranking button -->
      <v-btn block text color="primary" class="text-none mt-2 rounded-lg" style="border:1px solid #e3f2fd;" @click="showModal = true">
        View full ranking ({{ leaderboard.length }})
        <v-icon small right>mdi-chevron-right</v-icon>
      </v-btn>
    </v-card-text>

    <!-- Full Ranking Modal -->
    <v-dialog v-model="showModal" max-width="480" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pb-2">
          <div class="d-flex align-center">
            <v-icon color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
            <span class="subtitle-1 font-weight-black">Quarterly Leaderboard</span>
          </div>
          <v-btn icon small @click="showModal = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-3" style="max-height:70vh;overflow-y:auto;">
          <div v-for="(entry, i) in leaderboard" :key="entry.name"
            class="d-flex align-center pa-3 rounded-lg mb-2"
            :style="entry.isYou
              ? 'background:#fff8e1;border:1px solid #ffe082;'
              : 'background:#f5f5f5;border:1px solid #e0e0e0;'"
          >
            <div class="mr-3 d-flex align-center justify-center"
              style="width:36px;height:36px;border-radius:50%;flex-shrink:0;"
              :style="i === 0 ? 'background:#fff8e1;' : i === 1 ? 'background:#f0f0f0;' : i === 2 ? 'background:#fce4ec;' : 'background:#e0e0e0;'"
            >
              <v-icon v-if="i === 0" small color="amber darken-2">mdi-crown</v-icon>
              <v-icon v-else-if="i === 1" small color="grey">mdi-medal</v-icon>
              <v-icon v-else-if="i === 2" small color="pink lighten-2">mdi-medal-outline</v-icon>
              <span v-else class="caption grey--text font-weight-bold">{{ i + 1 }}</span>
            </div>
            <span class="body-2 flex-grow-1" :class="entry.isYou ? 'font-weight-black' : 'font-weight-medium'" style="cursor:pointer;" @click.stop="openProfile(entry)">
              {{ entry.name }}
              <v-chip v-if="entry.isYou" x-small color="amber lighten-3" class="ml-1" style="height:16px;">you</v-chip>
            </span>
            <div class="text-right">
              <span class="subtitle-2 font-weight-black" :class="entry.isYou ? 'primary--text' : ''">
                {{ entry.score.toLocaleString() }}
              </span>
              <span class="caption grey--text ml-1">pts</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- User Profile Dialog -->
    <v-dialog v-model="profileDialog" max-width="420">
      <v-card class="rounded-xl" v-if="profileUser">
        <v-card-text class="pa-6">
          <div class="d-flex align-start mb-4">
            <v-avatar color="primary" size="64" class="white--text text-h5 font-weight-medium mr-4">
              <v-img v-if="profileUser.profile_picture" :src="getProfilePictureUrl(profileUser.profile_picture)" />
              <span v-else>{{ getUserInitials(profileUser) }}</span>
            </v-avatar>
            <div class="flex-grow-1">
              <h3 class="text-h6 font-weight-bold mb-1">{{ profileUser.name }}</h3>
              <p class="text-subtitle-2 text--secondary mb-0 text-uppercase" style="font-size:11px;letter-spacing:0.05em;">
                {{ profileUser.position_name || 'No Position' }}
              </p>
            </div>
            <v-btn icon small @click="profileDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div v-if="profileUser.skills && profileUser.skills.length" class="mb-3">
            <div class="d-flex flex-wrap">
              <v-chip v-for="(skill, idx) in profileUser.skills.slice(0, 3)" :key="idx" x-small label class="mr-2 mb-2" color="primary" outlined>{{ skill }}</v-chip>
              <v-chip v-if="profileUser.skills.length > 3" x-small label class="mb-2">+{{ profileUser.skills.length - 3 }}</v-chip>
            </div>
          </div>
          <v-divider class="mb-3" />
          <div v-if="profileUser.role_members && profileUser.role_members.filter(m => m.role && ![1,2].includes(m.role.id)).length" class="d-flex align-center text-caption text--secondary">
            <v-icon x-small class="mr-1">mdi-account-group</v-icon>
            <span>Team: <strong>{{ profileUser.role_members.filter(m => m.role && ![1,2].includes(m.role.id)).map(m => m.role.name).join(', ') }}</strong></span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: 'QuarterlyLeaderboardCard',
  inject: {
    users: { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      showModal: false,
      leaderboard: [],
      profileDialog: false,
      profileUser: null,
    };
  },
  computed: {
    myUserId() {
      return this.$store.state.auth.user?.id;
    },
  },
  mounted() {
    this.load();
    this.$root.$on('growth:leaderboard-refresh', this.load);
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:leaderboard-refresh', this.load);
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const res = await this.$axios.get('api/employee-growth/leaderboard', { period: 'quarterly', _t: Date.now() });
        if (res.data.response) {
          this.leaderboard = res.data.data.map(e => ({ ...e, isYou: e.user_id === this.myUserId }));
        }
      } finally {
        this.loading = false;
      }
    },
    openProfile(entry) {
      const allUsers = typeof this.users === 'function' ? this.users() : [];
      this.profileUser = allUsers.find(u => u.id === entry.user_id) || null;
      if (this.profileUser) this.profileDialog = true;
    },
    getProfilePictureUrl(path) {
      return (process.env.VUE_APP_API_URL || '') + path;
    },
    getUserInitials(user) {
      if (!user || !user.name) return 'U';
      return user.name.split(' ').map(p => p[0]).join('').toUpperCase().substring(0, 2);
    },
  },
};
</script>
